import {
  BreakPointCondition,
  SliderModal,
  useAlert,
  useBreakpointIf,
  VStack,
} from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { useBreakpointValue } from '@genial/utils';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ScrollView } from 'native-base';
import React, { useEffect } from 'react';

import { useRecoilQuery } from '../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../hooks/useToastCreator';
import { SignedInRootStackParamList } from '../../../routes/types';
import { blockHistoryDetails } from '../../../store/blockList/selector';

import { CustomerCard } from './components/CustomerCard';
import { Header } from './components/Header';
import { ProcessHistory } from './components/ProcessHistory';

export namespace BlockDetailsTypes {
  export type Props = object;
}

export const BlockDetails: React.FC<
  NativeStackScreenProps<SignedInRootStackParamList, 'BlockDetails'>
> = ({ route }) => {
  const isMobile = useBreakpointIf(BreakPointCondition.EqualOrDown, 'sm');
  const { customerId, channel } = route.params;

  const {
    data: blockDetails,
    error: blockDetailsError,
    loading: blockDetailsLoading,
  } = useRecoilQuery(blockHistoryDetails({ customerId: parseInt(customerId, 10), channel }), {
    cachePolicy: 'no-cache',
  });

  const width = useBreakpointValue({
    base: 1,
    md: 0.42,
  });

  const statusIconCustomerUnlock = {
    statusNameAlias: blockDetails?.data.optout.statusNameAlias
      ? blockDetails?.data.optout.statusNameAlias
      : '',
    statusName: blockDetails?.data.optout.statusName ? blockDetails?.data.optout.statusName : '',
    statusColor: blockDetails?.data.optout.statusColor ? blockDetails?.data.optout.statusColor : '',
    statusBackgroundColor: blockDetails?.data.optout.statusBackground
      ? blockDetails?.data.optout.statusBackground
      : '',
    statusSize: blockDetails?.data.optout.statusSize ? blockDetails?.data.optout.statusSize : '',
    statusIcon: blockDetails?.data.optout.statusIcon ? blockDetails?.data.optout.statusIcon : '',
  };

  const { showToastFromError } = useToastCreator('blocklist-error');
  const { alert } = useAlert();

  useEffect(() => {
    if (blockDetailsError) {
      if (instanceOfErrorListResponse(blockDetailsError)) {
        (blockDetailsError as unknown as ErrorListResponse).errors?.forEach((errorItem) =>
          !isMobile
            ? showToastFromError(errorItem)
            : alert(
                {
                  icon: 'Warning',
                  iconColor: 'feedback.attention.default',
                  description: errorItem.errorMessage,
                },
                () => {}
              )
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockDetailsError]);

  return (
    <SliderModal dismissWhenClickOutside={false} width={width}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <VStack
          backgroundColor={'neutral.white'}
          p={Spacing['inline.xxs']}
          space={Spacing[isMobile ? 'stack.xxxs' : 'stack.xxs']}>
          <Header
            statusNameOptout={
              blockDetails?.data.optout.statusName ? blockDetails?.data.optout.statusName : ''
            }
            customerId={blockDetails?.data.customerId}
            status={statusIconCustomerUnlock}
            optoutId={blockDetails?.data.optout.optoutId ? blockDetails?.data.optout.optoutId : ''}
          />
          <CustomerCard
            customerName={blockDetails?.data.customerName ? blockDetails?.data.customerName : ''}
            lastUpdate={
              blockDetails?.data.updatedDateFormatted ? blockDetails?.data.updatedDateFormatted : ''
            }
          />
          <ProcessHistory
            loading={blockDetailsLoading}
            data={blockDetails?.data.historyNormalized || []}
          />
        </VStack>
      </ScrollView>
    </SliderModal>
  );
};
